import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { MapPin, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DeliveryZoneCheck — sits above the delivery-address input inside CartDrawer.
 *
 * The customer types their postcode, blur/enter triggers `POST /api/delivery/check`,
 * and the component renders one of:
 *   - loading spinner while the request is in flight
 *   - green success card with zone name, fee, min order, ETA
 *   - amber warning card with the "we don't deliver there yet" message
 *
 * When available it emits `onZoneChange(zone)` upwards so the parent can:
 *   - block checkout until minimum order is met
 *   - display the delivery fee inline in the cart totals
 */
export default function DeliveryZoneCheck({ initialPostcode = "", onZoneChange }) {
  const [postcode, setPostcode] = useState(initialPostcode);
  const [status, setStatus] = useState("idle"); // idle | loading | ok | out | error
  const [zone, setZone] = useState(null);
  const [msg, setMsg] = useState("");

  const check = useCallback(async (value) => {
    const pc = (value || "").replace(/[^0-9]/g, "").slice(0, 4);
    if (pc.length !== 4) {
      setStatus("idle");
      setZone(null);
      onZoneChange?.(null);
      return;
    }
    setStatus("loading");
    try {
      const { data } = await axios.post(`${API}/delivery/check`, { postcode: pc });
      if (data.available) {
        setStatus("ok");
        setZone(data);
        setMsg("");
        onZoneChange?.(data);
      } else {
        setStatus("out");
        setZone(null);
        setMsg(data.message || "We don't deliver to that postcode yet.");
        onZoneChange?.(null);
      }
    } catch {
      setStatus("error");
      setZone(null);
      setMsg("Couldn't check that postcode — please try again.");
      onZoneChange?.(null);
    }
  }, [onZoneChange]);

  // Debounce on typing so we don't hammer the endpoint per keystroke.
  useEffect(() => {
    const t = setTimeout(() => check(postcode), 350);
    return () => clearTimeout(t);
  }, [postcode, check]);

  return (
    <div className="mb-3" data-testid="delivery-zone-check">
      <label className="section-label mb-2 block">Delivery postcode</label>
      <div className="relative">
        <MapPin
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mm-cream)]/45"
        />
        <input
          type="text"
          inputMode="numeric"
          value={postcode}
          onChange={(e) => setPostcode(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
          placeholder="e.g. 4110"
          maxLength={4}
          data-testid="delivery-postcode-input"
          className="w-full bg-[var(--mm-bg-alt)]/70 border border-[var(--mm-cream)]/12 focus:border-[var(--mm-accent)] outline-none pl-9 pr-9 py-2.5 text-sm text-[var(--mm-cream)] tracking-wider placeholder:text-[var(--mm-cream)]/35"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {status === "loading" && <Loader2 size={13} className="animate-spin text-[var(--mm-accent)]" />}
          {status === "ok" && <CheckCircle2 size={13} className="text-emerald-500" />}
          {(status === "out" || status === "error") && <XCircle size={13} className="text-amber-500" />}
        </div>
      </div>

      {status === "ok" && zone && (
        <div
          className="mt-2 border border-emerald-500/30 bg-emerald-500/[0.06] px-3 py-2 text-xs text-[var(--mm-cream)]/85 leading-snug"
          data-testid="delivery-zone-ok"
        >
          <p>
            <span className="text-emerald-400 font-medium">{zone.zone_name}</span> — delivery{" "}
            <b>{zone.fee > 0 ? `$${zone.fee.toFixed(2)}` : "free"}</b> · ETA ~{zone.eta_min} min
          </p>
          {zone.min_order > 0 && (
            <p className="text-[var(--mm-cream)]/50 text-[11px] mt-0.5">
              Minimum order for delivery: <b>${zone.min_order.toFixed(2)}</b>
              {zone.free_over > 0 && zone.fee > 0 && (
                <> · Free delivery over <b>${zone.free_over.toFixed(2)}</b></>
              )}
            </p>
          )}
        </div>
      )}

      {(status === "out" || status === "error") && (
        <p
          className="mt-2 text-xs text-amber-400 leading-snug"
          data-testid="delivery-zone-out"
        >
          {msg}
        </p>
      )}
    </div>
  );
}
